// PROD-1079 (extension): no physician DRAFT plan reaches the patient before the first signing.
//
// md autosaves its working plan into cases.follow_up three seconds after the physician picks
// it (CaseDetailPage savePayload.follow_up: ordersPlaced, type, disposition, timeframe,
// observation and sign-out fields, referral, requirements), with no signed guard, and the
// API sends the column to the patient. The patient app read it as THE plan: the Deliver task
// list ("Medications: amoxicillin 500 mg" before the physician removed it at signing), the
// AI medication-review request, Further testing / Instructions, and the Deliver stage tick
// (a draft type "complete" turned it green).
//
// The rule (fix 2, decided by the main session 2026-10-16):
//   no signed visit at all (encounter 1 before its first signing): the column is md's draft.
//     Its plan keys are dropped, an unsigned row care_plan is not read, and the Deliver
//     screens redirect to Case Home (caseHasSignedVisit). Nothing signed can be lost here.
//   a signed visit exists (encounter 1 signed, or any follow-up N >= 2): the column, the row
//     care_plan and the observation positiveOutcome nesting are read exactly as at a95495a6.
//     ★ No care_plan remapping: md's nextPlan, from which the API builds row N+1's care_plan,
//     does not carry dme, diagnosticTests, referrals, dueDates, customDates, the facilities or
//     requirements, so reading the row instead of the column dropped SIGNED orders (review 2
//     H1, M1). RESIDUAL (MEDIUM): while md drafts follow-up N, md's autosave can put N's draft
//     into cases.follow_up, and a patient in N reads it. The proper fix is in md + API (md
//     nextPlan carries the signed ordersPlaced, the API copies it into row N+1's care_plan,
//     with a backfill or fallback for live cases); see P22_957/RESULT.md "Fix 2".
//
// Appointment fields the API writes into the same column (scheduledDate, appointmentStatus,
// reopened, ...) are not a plan and are always kept.
//
// ★ IMPORTS: utils/caseEncounters (the one owner of "which encounter") and ./signedVisit.js.
//   scripts/check-no-draft-to-patient.mjs loads this file in node. No request, no log.

import { currentEncounterNumber, encountersOf } from '../utils/caseEncounters' // the owner (check-encounter-owner ARM 3 spells it this way)
import { caseShowsFinalize } from './signedVisit.js'

const rowsOf = (c) => encountersOf(c).filter((e) => e && typeof e === 'object')
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {})

/** The md plan keys of cases.follow_up (the draft-able part). */
export const PLAN_FOLLOW_UP_KEYS = [
  'ordersPlaced', 'type', 'disposition', 'followUpPlan', 'requirements', 'referral',
  'referralSpecialty', 'referralNotes', 'timeframe', 'observationNotes', 'observationDuration',
  'parameters', 'escalationCriteria', 'redFlags', 'observationInterval', 'checkInSchedule',
  'checkInFrequency', 'positiveOutcome', 'negativeOutcome', 'signOutNotes', 'reasonsToReturn',
]

/**
 * Has the case a signed visit? The Deliver screens' route guard and the plan gate.
 *   - the patient stands in a follow-up (current encounter >= 2): only a signing creates row N+1;
 *   - else a finalized encounter row;
 *   - no rows (legacy, or a persisted case before loadCases answered): the legacy finalize rule
 *     (utils/signedVisit.js caseShowsFinalize), plus the MAPPED stamp completedAt (the store's
 *     case carries completedAt, the raw API case completed_at; L2 of review 2).
 */
export function caseHasSignedVisit(c) {
  if (currentEncounterNumber(c) >= 2) return true
  const rows = rowsOf(c)
  if (rows.length === 0) return caseShowsFinalize(c, null) || !!c?.completedAt
  return rows.some((e) => e.finalized === true)
}

/** The case's follow_up: as stored once a visit is signed; before that without the plan keys. */
export function planFollowUp(c) {
  let raw = c?.followUp || c?.follow_up
  if (typeof raw === 'string') { try { raw = JSON.parse(raw) } catch { raw = null } }
  const f = obj(raw)
  if (caseHasSignedVisit(c)) return f
  const out = { ...f }
  for (const k of PLAN_FOLLOW_UP_KEYS) delete out[k]
  return out
}

/** The orders the patient may act on: the column's ordersPlaced once a visit is signed, else {}. */
export function signedOrdersPlaced(c) {
  return obj(planFollowUp(c).ordersPlaced)
}
